import { batchReplaceSuperElement } from '@fs/zion-sls'

/**
 * Generates an array of relationship combos for a given person, excluding relationships related to events.
 *
 * @param {Object} person - The person object containing subElements and an id.
 * @param {Array} person.subElements - Array of sub-elements, which may include relationships.
 * @param {string|number} person.id - The unique identifier of the person.
 * @returns {Array<Object>} An array of combo objects
 */
export const getRelationshipCombos = (person) => {
  const relationships = person?.subElements?.filter((se) => se.elementType === 'RELATIONSHIP') || []
  const relationshipsNotRelatedToEvents = relationships.filter(
    (relationship) => relationship?.subElements?.some((se) => se.elementType === 'EVENT') === false
  )

  const combos = relationshipsNotRelatedToEvents
    .map((relationship) => {
      const { superElements, relType } = relationship || {}
      const otherPersonId = superElements?.find((superElement) => superElement?.id !== person?.id)?.id
      const combo = otherPersonId && relType ? `${otherPersonId}::${relType}` : null

      return {
        combo,
        relationshipId: relationship?.id || null,
        type: 'RELATIONSHIP',
      }
    })
    .filter((item) => item.combo)

  return combos
}

/**
 * Generates an array of relationship event combinations from the provided form values.
 *
 * Each combination represents a unique relationship event, identified by the combination
 * of `otherPersonId` and `eventRelType`, and includes metadata about its position and origin.
 *
 * @param {Object} formValues - The form values containing events data.
 * @param {Object} [formValues.events] - The events object containing primary and additional events.
 * @param {Array} [formValues.events.primary] - The array of primary event objects.
 * @param {Array} [formValues.events.additional] - The array of additional event objects.
 * @returns {Array<Object>} An array of objects
 */
export const getRelationshipEventsCombos = (formValues) => {
  const allEvents = [...(formValues?.events?.primary || []), ...(formValues?.events?.additional || [])]
  const primaryLength = formValues?.events?.primary?.length || 0

  // Only consider relationship events with both otherPerson and eventType
  const combos = allEvents
    .map((event, index) => {
      const otherPersonId = event?.otherPerson?.key
      const eventRelType = event?.eventType?.relType
      const isRelationshipEvent = event?.eventType?.isRelationshipEvent
      const combo = isRelationshipEvent && otherPersonId && eventRelType ? `${otherPersonId}::${eventRelType}` : null

      return {
        combo,
        position: index,
        isFromPrimaryList: index < primaryLength,
        relationshipId: event?.relationshipId || null, // This is the parent relationship's id of the event, if any
        type: 'EVENT',
      }
    })
    .filter((item) => item.combo)

  return combos
}

export const getDuplicatedRelationshipEvents = (formValues, person) => {
  const relationshipCombos = getRelationshipCombos(person)
  const relationshipEventCombos = getRelationshipEventsCombos(formValues)
  const allCombos = [...relationshipCombos, ...relationshipEventCombos]

  const seen = new Map()
  const duplicates = []
  allCombos.forEach((item) => {
    const currentSeen = seen.get(item.combo)
    if (currentSeen && currentSeen.type !== 'RELATIONSHIP') {
      duplicates.push(item)
    } else {
      seen.set(item.combo, item)
    }
  })

  return { duplicates, relationshipEventCombos, relationshipCombos }
}

/**
 * Replaces the "other person" in a relationship if it differs from the provided new person ID.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.relationship - The RELATIONSHIP object.
 * @param {string|number} params.personId - The ID of the current person. Not the one being replaced.
 * @param {string|number} params.otherPersonId - The ID of the new person to replace the original other person.
 * @returns {Array} An array of actions if a replacement is needed, otherwise an empty array.
 */
export const replaceRelationshipPerson = ({ relationship, personId, otherPersonId }) => {
  const originalOtherPersonSuperElement = relationship?.superElements?.find((f) => f.id !== personId)

  if (originalOtherPersonSuperElement && originalOtherPersonSuperElement.id !== otherPersonId) {
    return batchReplaceSuperElement({
      newSuperPartitionKey: relationship.partitionKey,
      oldSuperPartitionKey: relationship.partitionKey,
      subPartitionKey: relationship.partitionKey,
      oldSuperId: originalOtherPersonSuperElement.id,
      newSuperId: otherPersonId,
      subId: relationship.id,
      oldElementType: 'PERSON',
      newElementType: 'PERSON',
      extraArgs: { order: originalOtherPersonSuperElement.order },
    })
  }
  return []
}
